import React, { useMemo, useState } from 'react';
import { useQueries } from '@tanstack/react-query';

import {
  fetchSitrepById,
  keys,
  type PollSitrepResponse,
} from 'components/DisasterGPT/hooks/useSitrepsV2';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import Loader from 'components/Loader/Loader';
import CurrentReportsSitrepCard from 'components/Reports/CurrentReportsSitrepCard';
import type { GetSitrepsResponse } from 'components/Reports/reports.types';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';

/** Returns true if any section's name or text content matches the query. */
const sitrepMatchesSearch = (
  sitrepData: PollSitrepResponse | undefined,
  query: string
): boolean => {
  if (!sitrepData?.sections) return false;
  return sitrepData.sections.some(
    (section) =>
      section.name?.toLowerCase().includes(query) ||
      section.section_versions?.some((version) =>
        version.sitrep_section_text?.toLowerCase().includes(query)
      )
  );
};

type HistoricalReportViewProps = {
  historicalSitreps: GetSitrepsResponse[];
  isLoading: boolean;
  isError: boolean;
};

const HistoricalReportView: React.FC<HistoricalReportViewProps> = ({
  historicalSitreps,
  isLoading,
  isError,
}) => {
  const [searchQuery, setSearchQuery] = useState('');

  // Prefetch section data for all historical sitreps so we can search on text content.
  // These queries share the same cache keys as useSitrepV2 inside each card.
  const sitrepQueries = useQueries({
    queries: historicalSitreps.map((sitrep) => ({
      queryKey: keys.byId(sitrep.id),
      queryFn: () => fetchSitrepById(sitrep.id),
      staleTime: 5 * 60 * 1000,
    })),
  });

  const filteredSitreps = useMemo(() => {
    const trimmed = searchQuery.trim().toLowerCase();
    if (!trimmed) return historicalSitreps;

    return historicalSitreps.filter((sitrep, index) => {
      // Match on top-level sitrep fields
      if (sitrep.name?.toLowerCase().includes(trimmed)) return true;
      if (sitrep.description?.toLowerCase().includes(trimmed)) return true;

      // Match on section text content (available once prefetch resolves)
      const sitrepData = sitrepQueries[index]?.data;
      return sitrepMatchesSearch(sitrepData, trimmed);
    });
  }, [historicalSitreps, searchQuery, sitrepQueries]);

  return (
    <div className="reports-tabs pt-3">
      {isError ? (
        <div className="text-center mt-4">
          <span>Error loading reports.</span>
        </div>
      ) : isLoading ? (
        <Loader />
      ) : historicalSitreps.length > 0 ? (
        <>
          <div className="historical-search d-flex align-items-center gap-2 mb-3">
            <div className="position-relative flex-grow-1">
              <SharedIcon
                iconName="search"
                size="sm"
                classes="historical-search__icon"
              />
              <input
                type="text"
                className="form-control ps-5"
                placeholder="Search historical..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                aria-label="Search historical SITREPs"
              />
            </div>
            <StylishNewButton
              className="button--secondary button--icon"
              type="button"
              aria-label="Clear historical SITREP search"
              title="Clear search"
              disabled={!searchQuery.trim()}
              onClick={() => setSearchQuery('')}
            >
              <SharedIcon iconName="close" size="sm" aria-hidden="true" />
            </StylishNewButton>
          </div>

          {filteredSitreps.length > 0 ? (
            filteredSitreps.map((sitrep) => (
              <div key={sitrep.id} className="reports-tabs__latest-sitrep">
                <CurrentReportsSitrepCard sitrep={sitrep} hideDraftButton />
              </div>
            ))
          ) : (
            <div className="reports-tabs__header__empty">
              <p>No historical SITREPs match your search</p>
            </div>
          )}
        </>
      ) : (
        <div className="reports-tabs__header__empty">
          <p>No Historical SITREPs found</p>
        </div>
      )}
    </div>
  );
};

export default HistoricalReportView;
